import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { GATE_POSITIONS, WEST_PROPORTION, EAST_PROPORTION, TUNNELS } from './gatePositions';

const formatBuffer = (minutes) => {
  if (minutes == null) return null;
  const abs = Math.abs(minutes);
  const sign = minutes < 0 ? '-' : '';
  if (abs >= 60) {
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return m > 0 ? `${sign}${h}h ${m}m` : `${sign}${h}h`;
  }
  return `${sign}${abs}m`;
};

const getBufferColor = (minutes) => {
  if (minutes == null) return 'rgba(255,255,255,0.5)';
  if (minutes < 15) return '#f44336';
  if (minutes < 30) return '#F9B612';
  return '#4CAF50';
};

function RouteMapModal({ open, onClose, driver, inboundGate, inboundETA }) {
  const [revealedCount, setRevealedCount] = useState(0);

  const inboundGateNum = parseInt(inboundGate?.replace(/\D/g, ''), 10);

  // Check if timing data exists (solver stamped it)
  const flights = driver?.flights || [];
  const hasTimingData = flights.length > 0 && flights.some(f => f.expectedArrival);

  // Build route steps from driver's flights, marking cold sort vs reroutes vs gate stops.
  // Each entry: { type: 'gate', gateNum } or { type: 'cold_sort' } or { type: 'reroutes' }
  const routeSteps = flights.map(f => {
    if (f.connectionCategory === 'cold') {
      return { type: 'cold_sort' };
    }
    if (f.connectionCategory === 'cancelled') {
      return { type: 'reroutes' };
    }
    const raw = f.gate || '';
    return { type: 'gate', gateNum: parseInt(raw.replace(/\D/g, ''), 10) };
  });

  // Gate-only list for map highlighting
  const routeGates = routeSteps
    .filter(s => s.type === 'gate')
    .map(s => s.gateNum);

  // Track which step indices reach each off-concourse facility so the label lights up.
  const coldSortRevealed = routeSteps.some(
    (s, i) => s.type === 'cold_sort' && i < revealedCount
  );
  const reroutesRevealed = routeSteps.some(
    (s, i) => s.type === 'reroutes' && i < revealedCount
  );

  // All animation timers live here so close/unmount/replay can always
  // clear them — a cleanup returned inside setTimeout is discarded, and
  // unmanaged Replay intervals stack (visibly speeding up the reveal).
  const timersRef = React.useRef({ timeout: null, interval: null });

  const clearTimers = useCallback(() => {
    if (timersRef.current.timeout) clearTimeout(timersRef.current.timeout);
    if (timersRef.current.interval) clearInterval(timersRef.current.interval);
    timersRef.current = { timeout: null, interval: null };
  }, []);

  const startAnimation = useCallback((delay) => {
    clearTimers();
    setRevealedCount(0);
    timersRef.current.timeout = setTimeout(() => {
      let count = 0;
      timersRef.current.interval = setInterval(() => {
        count += 1;
        setRevealedCount(count);
        if (count >= routeSteps.length) {
          clearInterval(timersRef.current.interval);
          timersRef.current.interval = null;
        }
      }, 600);
    }, delay);
  }, [clearTimers, routeSteps.length]);

  // Animation effect
  useEffect(() => {
    if (!open || routeSteps.length === 0) {
      setRevealedCount(0);
      return;
    }

    startAnimation(300);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, driver]);

  const handleReplay = () => {
    startAnimation(100);
  };

  // Find this gate's step index in routeSteps (accounts for cold sort gaps)
  const getStepIndex = (gateNum) =>
    routeSteps.findIndex(s => s.type === 'gate' && s.gateNum === gateNum);

  const getGateColor = (gateNum) => {
    // Inbound gate is always gold
    if (gateNum === inboundGateNum) {
      return { bg: '#F9B612', text: '#000' };
    }

    // Check if this gate is in the route and has been revealed
    const stepIdx = getStepIndex(gateNum);
    if (stepIdx !== -1 && stepIdx < revealedCount) {
      return { bg: '#4CAF50', text: '#fff' };
    }

    // Default inactive
    return { bg: '#333', text: '#aaa' };
  };

  const getGateStyle = (gateNum) => {
    const colors = getGateColor(gateNum);
    const isInbound = gateNum === inboundGateNum;
    const stepIdx = getStepIndex(gateNum);
    const isRevealed = stepIdx !== -1 && stepIdx < revealedCount;

    return {
      width: 28,
      height: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bg,
      color: colors.text,
      borderRadius: '4px',
      fontSize: '0.8rem',
      fontWeight: isInbound || isRevealed ? 'bold' : 'normal',
      border: 'none',
      position: 'relative',
      transition: 'background-color 0.3s ease, transform 0.3s ease',
      transform: isRevealed ? 'scale(1.15)' : 'scale(1)',
    };
  };

  // Render a numbered badge for route stops
  const renderBadge = (gateNum, side) => {
    const stepIdx = getStepIndex(gateNum);
    if (stepIdx === -1 || stepIdx >= revealedCount) return null;

    const badgePosition = side === 'north' ? { top: -16 } : { bottom: -16 };

    return (
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          ...badgePosition,
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: '#4CAF50',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.65rem',
          fontWeight: 'bold',
          lineHeight: 1,
          zIndex: 2,
        }}
      >
        {stepIdx + 1}
      </Box>
    );
  };

  const renderGate = (gateNum) => {
    const pos = GATE_POSITIONS[gateNum];
    if (!pos) return null;

    return (
      <Box key={gateNum} sx={{ position: 'absolute', left: pos.left }}>
        <Box sx={{ position: 'relative' }}>
          {renderBadge(gateNum, pos.side)}
          <Box sx={getGateStyle(gateNum)}>{gateNum}</Box>
        </Box>
      </Box>
    );
  };

  const northWestGates = Object.keys(GATE_POSITIONS)
    .map(Number)
    .filter(g => GATE_POSITIONS[g].wing === 'west' && GATE_POSITIONS[g].side === 'north');
  const northEastGates = Object.keys(GATE_POSITIONS)
    .map(Number)
    .filter(g => GATE_POSITIONS[g].wing === 'east' && GATE_POSITIONS[g].side === 'north');
  const southWestGates = Object.keys(GATE_POSITIONS)
    .map(Number)
    .filter(g => GATE_POSITIONS[g].wing === 'west' && GATE_POSITIONS[g].side === 'south');
  const southEastGates = Object.keys(GATE_POSITIONS)
    .map(Number)
    .filter(g => GATE_POSITIONS[g].wing === 'east' && GATE_POSITIONS[g].side === 'south');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(30, 30, 30, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{ color: '#e2e2e2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" component="span" sx={{ color: '#e2e2e2' }}>
            Route Map — {driver?.name || 'Driver'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            {routeSteps.length} stop{routeSteps.length !== 1 ? 's' : ''} in route order
            {hasTimingData && driver?.routeStartTime && (
              <span style={{ marginLeft: 12 }}>
                Departs {driver.routeStartTime}
                {driver.routeEndTime && <> — Done {driver.routeEndTime}</>}
              </span>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<ReplayIcon />}
            onClick={handleReplay}
            sx={{ color: '#4CAF50', textTransform: 'none' }}
          >
            Replay
          </Button>
          <IconButton onClick={onClose} sx={{ color: '#aaa' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {/* Concourse Map */}
        <Box sx={{ position: 'relative', width: '100%', height: '220px', overflow: 'hidden' }}>
          {/* North Side (Odd gates) */}
          <Box sx={{ position: 'absolute', top: '40px', left: '0', width: '100%', display: 'flex' }}>
            {/* West wing */}
            <Box sx={{ display: 'flex', width: `${WEST_PROPORTION}%`, paddingRight: '10px', position: 'relative' }}>
              {northWestGates.map(g => renderGate(g))}
            </Box>
            {/* East wing */}
            <Box sx={{ display: 'flex', width: `${EAST_PROPORTION}%`, paddingLeft: '10px', position: 'relative' }}>
              {northEastGates.map(g => renderGate(g))}
            </Box>
          </Box>

          {/* Concourse Lines */}
          <Box sx={{ position: 'absolute', top: '70px', left: '0', width: `${WEST_PROPORTION - 1}%`, height: '3px', backgroundColor: '#e2e2e2' }} />
          <Box sx={{ position: 'absolute', top: '70px', right: '0', width: `${EAST_PROPORTION - 1}%`, height: '3px', backgroundColor: '#e2e2e2' }} />
          <Box sx={{ position: 'absolute', top: '130px', left: '0', width: `${WEST_PROPORTION - 1}%`, height: '3px', backgroundColor: '#e2e2e2' }} />
          <Box sx={{ position: 'absolute', top: '130px', right: '0', width: `${EAST_PROPORTION - 1}%`, height: '3px', backgroundColor: '#e2e2e2' }} />

          {/* South Side (Even gates) */}
          <Box sx={{ position: 'absolute', top: '135px', left: '0', width: '100%', display: 'flex' }}>
            {/* West wing */}
            <Box sx={{ display: 'flex', width: `${WEST_PROPORTION}%`, paddingRight: '10px', position: 'relative' }}>
              {southWestGates.map(g => renderGate(g))}
            </Box>
            {/* East wing */}
            <Box sx={{ display: 'flex', width: `${EAST_PROPORTION}%`, paddingLeft: '10px', position: 'relative' }}>
              {southEastGates.map(g => renderGate(g))}
            </Box>
          </Box>

          {/* Tunnels / Breezeways */}
          {TUNNELS.map((tunnel, index) => {
            let leftPosition;
            if (tunnel.section === 'west') {
              const relativePos = (tunnel.position - 23) / (39 - 23);
              leftPosition = `calc(${relativePos * WEST_PROPORTION}%)`;
            } else {
              const relativePos = (tunnel.position - 40) / (67 - 40);
              leftPosition = `calc(${WEST_PROPORTION}% + ${relativePos * EAST_PROPORTION}%)`;
            }
            return (
              <Box
                key={`tunnel-${index}`}
                sx={{
                  position: 'absolute',
                  left: leftPosition,
                  top: '70px',
                  transform: 'translateX(-7px)',
                }}
              >
                <Box
                  sx={{
                    width: tunnel.label === 'Breezeway' ? 16 : 20,
                    height: 60,
                    backgroundColor: '#e2e2e2',
                    borderRadius: 0,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: '-45px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: tunnel.label === 'Breezeway' ? '0.5rem' : '0.8rem',
                    fontWeight: 'bold',
                    color: '#f9b612',
                    textAlign: 'center',
                  }}
                >
                  {tunnel.label === 'West Tunnel' ? (
                    <>West<br />Tunnel</>
                  ) : tunnel.label === 'East Tunnel' ? (
                    <>East<br />Tunnel</>
                  ) : (
                    tunnel.label
                  )}
                </Box>
                {/* Cold Sort label on right side of West Tunnel */}
                {tunnel.label === 'West Tunnel' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: '-45px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: coldSortRevealed ? '#4CAF50' : 'rgba(255,255,255,0.25)',
                      textAlign: 'center',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    Cold<br />Sort
                  </Box>
                )}
                {/* Reroutes label on left side of East Tunnel */}
                {tunnel.label === 'East Tunnel' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '-105px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: reroutesRevealed ? '#FF5252' : 'rgba(255,255,255,0.25)',
                      textAlign: 'center',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    Reroutes
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Timing Table — only shown when solver has stamped timing data */}
        {hasTimingData && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                '& th, & td': {
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.8rem',
                  textAlign: 'left',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                },
                '& th': {
                  color: 'rgba(255,255,255,0.5)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                },
              }}
            >
              <thead>
                <tr>
                  <th>Stop</th>
                  <th>Gate</th>
                  <th>Flight</th>
                  <th>Arrive</th>
                  <th>ETD</th>
                  <th>Buffer</th>
                </tr>
              </thead>
              <tbody>
                {flights.map((f, idx) => {
                  const isCold = f.connectionCategory === 'cold';
                  const gateDisplay = isCold ? 'CS' : (f.gate || '—');
                  const etdDisplay = isCold ? '—' : (f.etd || '—');
                  const bufferVal = isCold ? null : f.bufferMinutes;
                  const bufferDisplay = isCold ? '—' : (bufferVal != null ? formatBuffer(bufferVal) : '—');
                  const bufferColor = isCold ? 'rgba(255,255,255,0.5)' : getBufferColor(bufferVal);

                  return (
                    <tr key={f.uniqueId || idx}>
                      <td style={{ color: '#e2e2e2', fontWeight: 600 }}>{idx + 1}</td>
                      <td style={{ color: '#e2e2e2' }}>{gateDisplay}</td>
                      <td style={{ color: 'rgba(255,255,255,0.7)' }}>
                        {f.flightNumber || '—'}
                        {f.splitType && <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 4, fontSize: '0.7rem' }}>({f.splitType})</span>}
                      </td>
                      <td style={{ color: '#e2e2e2', fontWeight: 500 }}>{f.expectedArrival || '—'}</td>
                      <td style={{ color: 'rgba(255,255,255,0.7)' }}>{etdDisplay}</td>
                      <td style={{ color: bufferColor, fontWeight: 600 }}>{bufferDisplay}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        )}

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#F9B612', mr: 1, borderRadius: '2px' }} />
            <Typography sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
              Inbound Gate
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 16, height: 16, backgroundColor: '#4CAF50', mr: 1, borderRadius: '2px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.6rem', color: '#fff', fontWeight: 'bold'
            }}>
              #
            </Box>
            <Typography sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
              Driver's Route (in order)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#333', mr: 1, borderRadius: '2px' }} />
            <Typography sx={{ color: '#e2e2e2', fontSize: '0.75rem' }}>
              Not on this route
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default RouteMapModal;
